import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { map, type Observable } from 'rxjs';

import type {
  GetImportResponse,
  ImportConfigurationRequest,
  ImportFileResponse,
  ImportRequest,
  ImportResponse,
  LoadImportStagingFieldsRequest,
  LoadImportStagingFieldsResponse,
} from '#/app/shared/domain/import/import.model';
import type { ImportColumnHeadersResponse } from '#/app/shared/domain/import/import-column-headers.model';
import type { ImportValidationResponse } from '#/app/shared/domain/import/import-validation.model';
import { assertIsDefined } from '#/app/shared/domain/types';
import { TenantContext } from '#/app/shared/services/tenant.context';

import type { UploadResponseWithAutoUploadId } from '../../domain/ui/file-uploader.model';
import { DataInvalidationService } from '../data-invalidation.service';

@Injectable({
  providedIn: 'root',
})
export class ImportService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  importResource(importId: Signal<string>) {
    return httpResource<GetImportResponse>(() => {
      this.#dataInvalidationService.track(['imports', 'imports:summary']);
      const tenantId = this.#tenantContext.currentTenantId();
      const importIdValue = importId();
      return tenantId && importIdValue
        ? `/api/tenants/${tenantId}/imports/${importIdValue}`
        : undefined;
    });
  }

  columnHeadersResource(importId: Signal<string | null>) {
    return httpResource<ImportColumnHeadersResponse>(() => {
      this.#dataInvalidationService.track('imports');
      const tenantId = this.#tenantContext.currentTenantId();
      const importIdValue = importId();
      return tenantId && importIdValue
        ? `/api/tenants/${tenantId}/imports/${importIdValue}/column-headers`
        : undefined;
    });
  }

  getImportColumnHeaders(
    importId: string,
  ): Observable<ImportColumnHeadersResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.get<ImportColumnHeadersResponse>(
      `/api/tenants/${tenantId}/imports/${importId}/column-headers`,
    );
  }

  createImport(request: ImportRequest): Observable<ImportResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<ImportResponse>(
      `/api/tenants/${tenantId}/imports`,
      request,
    );
  }

  updateImport(
    importId: string,
    request: ImportRequest,
  ): Observable<ImportResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.put<ImportResponse>(
      `/api/tenants/${tenantId}/imports/${importId}`,
      request,
    );
  }

  uploadImportFile(importId: string, file: File, autoUploadId?: string) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    const formData = new FormData();
    formData.append('ImportFile', file);

    return this.#httpClient
      .post<ImportFileResponse>(
        `/api/tenants/${tenantId}/imports/${importId}/files`,
        formData,
      )
      .pipe(
        map((response) => {
          return {
            ...response,
            autoUploadId,
          } as UploadResponseWithAutoUploadId<ImportFileResponse>;
        }),
      );
  }

  validateImportFiles(importId: string): Observable<ImportValidationResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<ImportValidationResponse>(
      `/api/tenants/${tenantId}/imports/${importId}/validate`,
      {},
    );
  }

  cancelImport(importId: string): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.delete<never>(
      `/api/tenants/${tenantId}/imports/${importId}`,
    );
  }

  deleteImportFile(importId: string, importFileId: string): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.delete<never>(
      `/api/tenants/${tenantId}/imports/${importId}/files/${importFileId}`,
    );
  }

  loadImportStagingFields(
    importId: string,
    request: LoadImportStagingFieldsRequest,
  ): Observable<LoadImportStagingFieldsResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<LoadImportStagingFieldsResponse>(
      `/api/tenants/${tenantId}/imports/${importId}/load`,
      request,
    );
  }

  finalizeImport(importId: string) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post(
      `/api/tenants/${tenantId}/imports/${importId}/finalize`,
      null,
    );
  }

  retryImport(importId: string): Observable<LoadImportStagingFieldsResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<LoadImportStagingFieldsResponse>(
      `/api/tenants/${tenantId}/imports/${importId}/retry`,
      null,
    );
  }

  updateImportConfiguration(
    importId: string,
    request: ImportConfigurationRequest,
  ): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);

    return this.#httpClient.post<never>(
      `/api/tenants/${tenantId}/imports/${importId}/configuration`,
      request,
    );
  }
}
